import { useEffect, useRef, useState } from 'react';
import { monotonicNow } from '../contexts/AuthContext';
import { useScreenRequest } from './useScreenRequest';

// #583 S6b1, design §7.5: data that has not been reloaded successfully for 60 seconds is stale.
export const STALE_AFTER_MS = 60_000;

// A repeated unchanged read cannot fix these refusals. 408/429 remain retryable; 401 is
// handled by the session owner in api/client.js before a screen can offer another action.
export const isReadRefusal = (error) => [400, 403, 404, 422].includes(error?.status);

/**
 * True once `loadedAt` (a monotonicNow() reading) is 60 s old. Elapsed time comes from the session
 * machine's monotonic clock, re-read when the timer fires and on every render, so neither a
 * wall-clock jump nor a timer delayed by a sleeping browser can keep old data looking fresh.
 */
export function useAged(loadedAt) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (loadedAt === null) return undefined;
    let timer;
    const check = () => {
      const left = loadedAt + STALE_AFTER_MS - monotonicNow();
      if (left <= 0) setTick((n) => n + 1);
      else timer = setTimeout(check, left);
    };
    check();
    return () => clearTimeout(timer);
  }, [loadedAt]);
  return loadedAt !== null && monotonicNow() - loadedAt >= STALE_AFTER_MS;
}

/**
 * One screen read with the common states. `data` is the answer to THIS query only. A failed or
 * aged reload of it keeps the data and reports `stale` ('failed' or 'aged'); the screen disables
 * its actions until a reload succeeds. A failure with nothing worth keeping is `error` alone.
 */
export function useScreenData(queryKey, fetch, { enabled = true, keeps = () => true } = {}) {
  const { read } = useScreenRequest(queryKey);
  const fetchRef = useRef(fetch);
  fetchRef.current = fetch;
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ loading: enabled, error: null, result: null });
  // A new query is loading from its first render (S6b1 review M3). Set in the effect below, the
  // previous query's error, or a blank area, would be painted for one frame first.
  const [shownKey, setShownKey] = useState(queryKey);
  if (shownKey !== queryKey) {
    setShownKey(queryKey);
    setState((s) => ({ ...s, loading: enabled, error: null }));
  }

  useEffect(() => {
    if (!enabled) {
      setState((s) => ({ ...s, loading: false, error: null }));
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    read((options) => fetchRef.current(options), {
      success: (data) => setState((s) => ({ ...s, result: { queryKey, data, loadedAt: new Date(), at: monotonicNow() } })),
      error: (error) => setState((s) => ({ ...s, error })),
      settled: () => setState((s) => ({ ...s, loading: false })),
    });
  }, [read, attempt, enabled, queryKey]);

  const current = enabled && state.result?.queryKey === queryKey ? state.result : null;
  const aged = useAged(current ? current.at : null);
  const kept = Boolean(current && keeps(current.data) && !isReadRefusal(state.error));
  const failed = Boolean(state.error) && kept;
  const shown = state.error && !kept ? null : current;
  const fresh = useRef(false);
  fresh.current = !state.loading && Boolean(current) && !state.error;
  return {
    // Recheck elapsed time at dispatch even if the stale timer has not had a turn yet.
    isFresh: () => fresh.current && current !== null && monotonicNow() - current.at < STALE_AFTER_MS,
    loading: state.loading,
    error: failed ? null : state.error,
    data: state.loading ? null : shown?.data ?? null,
    loadedAt: shown?.loadedAt ?? null,
    stale: state.loading || !shown ? null : failed ? 'failed' : aged ? 'aged' : null,
    // Loading from the same render as the request (#583 S6b2 fix round 1, M4): a screen that waits
    // for the re-read, such as an action's outcome, can never see one frame of "not loading" first.
    reload: () => {
      fresh.current = false;
      setAttempt((n) => n + 1);
      if (enabled) setState((s) => ({ ...s, loading: true, error: null }));
    },
  };
}
